import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Product } from "../../types/productTypes";
import api from "../../config/Api";

const API_URL = "/products";

// Paginated API response structure (Spring Data / standard API format)
interface PaginatedProductResponse {
  content: Product[];
  totalPages: number;
  totalElements: number;
  size?: number;
  number?: number;
}

// Thunks
export const fetchProductById = createAsyncThunk<Product, number>(
  "products/fetchProductById",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await api.get<Product>(`${API_URL}/${productId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const searchProduct = createAsyncThunk<Product[], string>(
  "products/searchProduct",
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get<Product[]>(`${API_URL}/search`, {
        params: { query },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchProductsBySellerId = createAsyncThunk<Product[], number>(
  "products/fetchProductsBySellerId",
  async (sellerId, { rejectWithValue }) => {
    try {
      // Changed from `${API_URL}/seller/${sellerId}` to `/sellers/products/seller/${sellerId}`
      const response = await api.get<Product[]>(`/sellers/products/seller/${sellerId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllProducts = createAsyncThunk<
  PaginatedProductResponse | Product[],
  Record<string, any> | undefined
>(
  "products/fetchAllProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${API_URL}`, {
        params: {
          ...params,
          pageNumber: params?.pageNumber ?? 0,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// State Interface
interface ProductState {
  product: Product | null;
  products: Product[];
  sellerProducts: Product[]; // Separated to prevent overwriting catalog state
  totalPages: number;
  totalElements: number;
  loading: boolean;
  error: string | null;
  searchProduct: Product[];
}

const initialState: ProductState = {
  product: null,
  products: [],
  sellerProducts: [],
  totalPages: 1,
  totalElements: 0,
  loading: false,
  error: null,
  searchProduct: [],
};

const customerProductSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSearch: (state) => {
      state.searchProduct = [];
    },
    clearSelectedProduct: (state) => {
      state.product = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Product By ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action: PayloadAction<Product>) => {
        state.loading = false;
        state.product = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch All Products (Paginated or Array)
      .addCase(fetchAllProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        if (payload && "content" in payload && Array.isArray(payload.content)) {
          state.products = payload.content;
          state.totalPages = payload.totalPages || 1;
          state.totalElements = payload.totalElements || payload.content.length;
        } else if (Array.isArray(payload)) {
          state.products = payload;
          state.totalPages = 1;
          state.totalElements = payload.length;
        } else {
          state.products = [];
          state.totalPages = 1;
          state.totalElements = 0;
        }
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Search Products
      .addCase(searchProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchProduct.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.loading = false;
        state.searchProduct = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(searchProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Seller Products (Saves directly to sellerProducts)
      .addCase(fetchProductsBySellerId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsBySellerId.fulfilled, (state, action) => {
        state.loading = false;
        state.sellerProducts = action.payload;
      })
      .addCase(fetchProductsBySellerId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSearch, clearSelectedProduct } = customerProductSlice.actions;

export default customerProductSlice.reducer;